// Incidencias: los avisos que abren los inquilinos desde su portal.
//
// Es una bandeja de trabajo, no un archivo: por defecto se ven las abiertas y
// las que están en marcha, porque son las que piden algo. Las resueltas se
// consultan cuando hacen falta.
import { useState } from 'react';
import { AlertTriangle, Wrench } from 'lucide-react';
import clsx from 'clsx';
import { useActualizarIncidencia, useIncidencias, type Incidencia } from '../api/portal';
import { useClients } from '../api/clients';
import { useProperties } from '../api/properties';
import { AyudaGuia } from '../components/Ayuda';

const CATEGORIAS: Record<string, string> = {
  fontaneria: 'Fontanería',
  electricidad: 'Electricidad',
  calefaccion: 'Calefacción / ACS',
  electrodomesticos: 'Electrodomésticos',
  humedades: 'Humedades',
  cerrajeria: 'Cerrajería',
  otros: 'Otros',
};

const ESTADOS: { id: Incidencia['estado']; etiqueta: string; clase: string }[] = [
  { id: 'abierta', etiqueta: 'Abierta', clase: 'bg-amber-100 text-amber-900' },
  { id: 'en_curso', etiqueta: 'En curso', clase: 'bg-sky-100 text-sky-900' },
  { id: 'resuelta', etiqueta: 'Resuelta', clase: 'bg-leaf/40 text-deep' },
];

export default function IncidenciasView() {
  const { data: items, isLoading, isError, error, refetch } = useIncidencias();
  const { data: clientes } = useClients();
  const { data: viviendas } = useProperties();
  const [verResueltas, setVerResueltas] = useState(false);

  const nombre = (id: string) =>
    clientes?.find((c) => c.client_id === id)?.nombre ?? '—';
  const direccion = (id: string) =>
    viviendas?.find((p) => p.property_id === id)?.direccion ?? '—';

  const visibles = (items ?? []).filter(
    (i) => verResueltas || i.estado !== 'resuelta',
  );
  const abiertas = items?.filter((i) => i.estado === 'abierta').length ?? 0;

  return (
    <div className="pb-16 md:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-extrabold tracking-tight text-deep">
            Incidencias
            {abiertas > 0 && (
              <span className="ml-2 rounded-full bg-amber-200 px-2.5 py-0.5 align-middle text-sm font-bold text-amber-900">
                {abiertas} {abiertas === 1 ? 'abierta' : 'abiertas'}
              </span>
            )}
          </h1>
          <AyudaGuia seccion="incidencias" />
        </div>
        <label className="flex items-center gap-2 text-sm font-semibold text-ink/60">
          <input
            type="checkbox"
            checked={verResueltas}
            onChange={(e) => setVerResueltas(e.target.checked)}
            className="h-4 w-4 accent-brand"
          />
          Ver también las resueltas
        </label>
      </div>

      {isLoading ? (
        <p className="mt-10 text-sm text-ink/50">Cargando…</p>
      ) : isError ? (
        /* Igual que en Viviendas: un fallo del servidor no puede disfrazarse de
           bandeja vacía, o parecerá que no hay incidencias cuando sí las hay. */
        <div className="mt-8 rounded-2xl border border-dashed border-red-300 bg-red-50 px-6 py-12 text-center">
          <AlertTriangle className="mx-auto text-red-500" size={28} strokeWidth={1.5} />
          <p className="mt-3 font-bold text-red-700">No se han podido cargar las incidencias</p>
          <p className="mt-1 text-sm text-red-600/80">
            Es un fallo del servidor, no es que no haya ninguna. {(error as Error)?.message}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-4 rounded-full bg-red-600 px-5 py-2 text-sm font-bold text-white transition hover:bg-red-700"
          >
            Reintentar
          </button>
        </div>
      ) : !visibles.length ? (
        <div className="mt-8 rounded-2xl border border-dashed border-deep/20 bg-surface px-6 py-14 text-center">
          <Wrench className="mx-auto text-brand" size={28} strokeWidth={1.25} />
          <p className="mt-3 text-sm text-ink/60">
            {items?.length
              ? 'No queda ninguna incidencia pendiente.'
              : 'Todavía no ha llegado ningún aviso desde el portal.'}
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {visibles.map((i) => (
            <Ficha
              key={i.incident_id}
              inc={i}
              cliente={nombre(i.client_id)}
              direccion={direccion(i.property_id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Ficha({
  inc,
  cliente,
  direccion,
}: {
  inc: Incidencia;
  cliente: string;
  direccion: string;
}) {
  const actualizar = useActualizarIncidencia();
  const [respuesta, setRespuesta] = useState(inc.respuesta ?? '');
  const estado = ESTADOS.find((e) => e.id === inc.estado) ?? ESTADOS[0];

  return (
    <article className="rounded-2xl border border-deep/10 bg-surface p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-bold text-deep">
            {CATEGORIAS[inc.categoria] ?? 'Aviso'} · {direccion}
          </p>
          <p className="text-sm text-ink/55">
            {cliente} · {new Date(Number(inc.created_at) * 1000).toLocaleDateString('es-ES')}
          </p>
        </div>
        <span
          className={clsx(
            'rounded-full px-3 py-1 text-[0.68rem] font-bold uppercase tracking-wider',
            estado.clase,
          )}
        >
          {estado.etiqueta}
        </span>
      </div>

      <p className="mt-3 whitespace-pre-line text-sm text-ink/75">{inc.descripcion}</p>

      {Boolean(inc.fotos?.length) && (
        <p className="mt-2 text-sm text-ink/50">
          {inc.fotos!.length} {inc.fotos!.length === 1 ? 'foto adjunta' : 'fotos adjuntas'}
        </p>
      )}

      <div className="mt-4 space-y-3 border-t border-deep/5 pt-4">
        <div className="flex flex-wrap gap-2">
          {ESTADOS.map((e) => (
            <button
              key={e.id}
              disabled={actualizar.isPending || e.id === inc.estado}
              onClick={() =>
                actualizar.mutate({ incidentId: inc.incident_id, estado: e.id })
              }
              className={clsx(
                'rounded-full px-4 py-1.5 text-sm font-bold transition',
                e.id === inc.estado
                  ? 'bg-deep text-white'
                  : 'border border-deep/15 text-deep hover:border-brand hover:text-brand',
              )}
            >
              {e.etiqueta}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          {/* Lo que se escriba aquí lo lee el inquilino tal cual en su portal. */}
          <input
            value={respuesta}
            onChange={(e) => setRespuesta(e.target.value)}
            placeholder="Respuesta para el inquilino (la lee él tal cual)"
            className="min-w-0 flex-1 rounded-xl border border-deep/15 px-3 py-2 text-sm outline-none focus:border-brand"
          />
          <button
            disabled={actualizar.isPending || respuesta === (inc.respuesta ?? '')}
            onClick={() =>
              actualizar.mutate({ incidentId: inc.incident_id, respuesta })
            }
            className="rounded-full bg-brand px-4 py-2 text-sm font-bold text-white transition hover:bg-deep disabled:opacity-40"
          >
            Enviar
          </button>
        </div>
      </div>
    </article>
  );
}
